import { useState } from "react";
import { useParams } from "react-router";
import {
  IconEyeOff,
  IconLock,
  IconLockOpen,
  IconPlus,
  IconSpeakerphone,
} from "@tabler/icons-react";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { useEvent } from "@/api/hooks/use-events";
import { useScheduleBoard } from "@/api/hooks/use-schedule";
import { useScheduleActions } from "@/hooks/use-schedule-actions";
import { useAuth } from "@/providers/auth-provider";
import { asEventId } from "@/types/identifiers";
import { formatDateTime24h } from "@/lib/time";
import { localDateKey, nowInZone, scheduleStatusMeta, type ScheduleStatus } from "@/domain/schedule";
import type { ScheduleSlot } from "@/api/hooks/use-schedule";
import { ScheduleDays } from "./schedule-days";
import { SchedulePlanner } from "./schedule-planner";
import { ScheduleSlotDialog } from "./schedule-slot-dialog";

const STATUS_ORDER: ScheduleStatus[] = ["planned", "in_progress", "overdue", "done"];

interface DialogState {
  open: boolean;
  slot: ScheduleSlot | null;
  buildId: string | null;
  presetStart?: string | null;
}

export function SchedulePage() {
  const { eventId: rawEventId } = useParams<{ eventId: string }>();
  const eventId = asEventId(rawEventId ?? "");
  const { can } = useAuth();
  const canManage = can("manage-schedule");
  const canViewBuilds = can("view-builds");

  const { data: event } = useEvent(eventId);
  const { data: board, isLoading } = useScheduleBoard(eventId);
  const actions = useScheduleActions(eventId);
  const teams = board?.teams;
  const releasedAt = board?.schedule_released_at;
  const lockedAt = board?.schedule_locked_at;
  // Viewers get nothing until ops releases; managers see the draft board.
  const scheduleHidden = !isLoading && !canManage && !releasedAt;
  // Freezing takes editing away from managers too — that is the point of it.
  const canEdit = canManage && !lockedAt;

  const [dialog, setDialog] = useState<DialogState>({ open: false, slot: null, buildId: null });
  const [view, setView] = useState<"planner" | "days">("planner");

  const allSlots = (teams ?? []).flatMap((t) => t.slots);
  const counts = STATUS_ORDER.map((status) => ({
    status,
    n: allSlots.filter((s) => s.status === status).length,
  }));

  // Open on today when it falls inside the event window; otherwise the first day
  // in the window that has slots, else the start of the window.
  const timeZone = event?.timezone ?? undefined;
  const today = nowInZone(timeZone).dateKey;
  const eventStart = event?.start_date;
  const eventEnd = event?.end_date;
  const scheduledDays = allSlots
    .map((s) => s.scheduled_start)
    .filter((v): v is string => Boolean(v))
    .map((iso) => localDateKey(iso))
    .sort();
  // Derive the range exactly as the planner does so the picked day is a real tile.
  const slotDays = [...new Set([today, ...scheduledDays])].sort();
  const rangeStart = eventStart ?? slotDays[0] ?? today;
  const rangeEnd = eventEnd ?? slotDays[slotDays.length - 1] ?? today;
  const todayInRange = today >= rangeStart && today <= rangeEnd;
  const firstSlotDayInRange = scheduledDays.find((d) => d >= rangeStart && d <= rangeEnd);
  const initialDayKey = todayInRange ? today : (firstSlotDayInRange ?? rangeStart);

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <PageHeader
        title="Schedule"
        subtitle={event?.name}
        action={
          canManage ? (
            <div className="flex items-center gap-2">
              {board && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actions.isReleasing}
                  onClick={() => {
                    const question = releasedAt
                      ? "Withdraw the schedule? Teams lose sight of the board until you release it again — the slots themselves are kept."
                      : "Release the schedule to teams? Viewers will see it from now on.";
                    if (window.confirm(question)) {
                      void actions.handleSetRelease(!releasedAt);
                    }
                  }}
                >
                  {releasedAt ? (
                    <>
                      <IconEyeOff className="h-4 w-4" /> Withdraw
                    </>
                  ) : (
                    <>
                      <IconSpeakerphone className="h-4 w-4" /> Release to teams
                    </>
                  )}
                </Button>
              )}
              {board && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={actions.isLocking}
                  onClick={() => void actions.handleSetLock(!lockedAt)}
                >
                  {lockedAt ? (
                    <>
                      <IconLockOpen className="h-4 w-4" /> Unfreeze
                    </>
                  ) : (
                    <>
                      <IconLock className="h-4 w-4" /> Freeze
                    </>
                  )}
                </Button>
              )}
              <Button
                size="sm"
                disabled={!canEdit}
                title={lockedAt ? "Schedule is frozen — unfreeze to add slots" : undefined}
                onClick={() => setDialog({ open: true, slot: null, buildId: null })}
              >
                <IconPlus className="h-4 w-4" /> Add slot
              </Button>
            </div>
          ) : undefined
        }
      />

      {!scheduleHidden && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {counts.map(({ status, n }) => {
            const m = scheduleStatusMeta(status);
            return (
              <div key={status} className={`bg-card border-l-3 rounded-r-lg ${m.border} py-3 pl-4 pr-5`}>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                  {m.label}
                </div>
                <div className="text-lg font-bold">{n}</div>
              </div>
            );
          })}
        </div>
      )}

      {isLoading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : scheduleHidden ? (
        <div className="text-sm text-muted-foreground">
          The schedule for this event hasn't been released yet.
        </div>
      ) : (teams ?? []).length === 0 ? (
        <div className="text-sm text-muted-foreground">No boats for this event.</div>
      ) : (
        <div className="space-y-3">
          {canManage && !releasedAt && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-700 dark:text-amber-400">
              Draft — teams can't see this schedule until you release it.
            </div>
          )}
          {releasedAt && (
            <p className="text-xs text-muted-foreground">
              Released to teams on {formatDateTime24h(releasedAt)}
            </p>
          )}
          {canManage && lockedAt && (
            <div className="rounded-md border border-border bg-muted px-3 py-2 text-xs font-medium text-muted-foreground">
              Frozen — slots can't be added, moved or removed until you unfreeze.
            </div>
          )}
          <div className="inline-flex rounded-md border border-border p-0.5 text-xs">
            {(["planner", "days"] as const).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded px-2.5 py-1 capitalize ${
                  view === v ? "bg-muted font-medium" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {v}
              </button>
            ))}
          </div>

          {view === "planner" ? (
            // Re-init the planner's day/window state when switching events.
            <SchedulePlanner
              key={eventId}
              teams={teams ?? []}
              canManage={canEdit}
              canViewBuilds={canViewBuilds}
              actions={actions}
              eventId={eventId}
              eventStart={eventStart}
              eventEnd={eventEnd}
              timeZone={timeZone}
              initialDayKey={initialDayKey}
              onEditSlot={(slot) => setDialog({ open: true, slot, buildId: slot.event_build_id })}
              onCreateAt={(buildId, presetStart) =>
                setDialog({ open: true, slot: null, buildId, presetStart })
              }
            />
          ) : (
            <ScheduleDays
              teams={teams ?? []}
              canManage={canEdit}
              canViewBuilds={canViewBuilds}
              eventId={eventId}
              timeZone={timeZone}
              onEdit={(slot) => setDialog({ open: true, slot, buildId: slot.event_build_id })}
            />
          )}
        </div>
      )}

      <ScheduleSlotDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        teams={teams ?? []}
        slot={dialog.slot}
        buildId={dialog.buildId}
        presetStart={dialog.presetStart}
        actions={actions}
      />
    </div>
  );
}
